import { useState, useEffect, useCallback, useRef } from 'react';
import {
  api,
  ModelDefinition,
  ModelProvider,
  ParameterDefinition,
} from '../api';

/**
 * Options for the useModelDefinition hook
 */
export interface UseModelDefinitionOptions {
  /** Entity ID to fetch by ID */
  entityId?: string;
  /** Provider to lookup by provider and model name */
  provider?: ModelProvider;
  /** Model name to lookup by provider and model name */
  modelName?: string;
  /** Auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

/**
 * Result returned by the useModelDefinition hook
 */
export interface UseModelDefinitionResult {
  /** The model definition (if loaded) */
  definition: ModelDefinition | null;
  /** The parameter schema from the definition */
  parameterSchema: ParameterDefinition[];
  /** Whether the model supports thinking mode */
  supportsThinking: boolean;
  /** Whether the model is a reasoning model */
  isReasoningModel: boolean;
  /** Maximum output tokens */
  maxOutputTokens: number;
  /** Loading state */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually refresh the definition */
  refresh: () => Promise<void>;
}

/**
 * React hook for fetching a Model Definition Entity by ID or provider/modelName lookup.
 *
 * Returns the full model definition including parameter schema, which can be used
 * to render data-driven parameter controls.
 *
 * @example
 * ```tsx
 * function ParameterEditor({ modelId }) {
 *   const { definition, parameterSchema, loading, error } = useModelDefinition({
 *     entityId: modelId,
 *   });
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *   if (!definition) return null;
 *
 *   return (
 *     <EntityParameterPanel
 *       parameterSchema={parameterSchema}
 *       values={values}
 *       onChange={setValues}
 *     />
 *   );
 * }
 * ```
 *
 * @example Lookup by provider and model name
 * ```tsx
 * const { definition } = useModelDefinition({
 *   provider: 'openai',
 *   modelName: 'gpt-5.2-2025-12-11',
 * });
 * ```
 */
export function useModelDefinition(
  options: UseModelDefinitionOptions = {}
): UseModelDefinitionResult {
  const { entityId, provider, modelName, autoFetch = true } = options;

  const [definition, setDefinition] = useState<ModelDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track the current fetch to avoid race conditions
  const fetchIdRef = useRef(0);

  const fetchDefinition = useCallback(async () => {
    // Determine what to fetch
    if (!entityId && (!provider || !modelName)) {
      setDefinition(null);
      return;
    }

    const currentFetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      let result: ModelDefinition;

      if (entityId) {
        result = await api.getModelDefinition(entityId);
      } else {
        result = await api.getModelDefinitionByLookup(provider!, modelName!);
      }

      // Only update if this is still the current fetch
      if (currentFetchId === fetchIdRef.current) {
        setDefinition(result);
      }
    } catch (err) {
      if (currentFetchId === fetchIdRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setDefinition(null);
      }
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [entityId, provider, modelName]);

  // Auto-fetch when dependencies change
  useEffect(() => {
    if (autoFetch && (entityId || (provider && modelName))) {
      fetchDefinition();
    }
  }, [autoFetch, entityId, provider, modelName, fetchDefinition]);

  // Clear definition when no identifiers are provided
  useEffect(() => {
    if (!entityId && (!provider || !modelName)) {
      setDefinition(null);
      setError(null);
      setLoading(false);
    }
  }, [entityId, provider, modelName]);

  return {
    definition,
    parameterSchema: definition?.parameterSchema || [],
    supportsThinking: definition?.capabilities?.supportsThinking || false,
    isReasoningModel: definition?.capabilities?.isReasoningModel || false,
    maxOutputTokens: definition?.capabilities?.maxOutputTokens || 4096,
    loading,
    error,
    refresh: fetchDefinition,
  };
}

/**
 * Creates default parameter values from a parameter schema
 */
export function createDefaultParameterValues(
  schema: ParameterDefinition[]
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const param of schema) {
    // Get default from explicit defaultValue or range.default
    let defaultValue: unknown;

    if (param.defaultValue !== undefined) {
      defaultValue = param.defaultValue;
    } else if (param.type === 'number' && param.range) {
      defaultValue = param.range.default;
    } else if (param.type === 'enum' && param.options && param.options.length > 0) {
      defaultValue = param.options[0].value;
    } else if (param.type === 'boolean') {
      defaultValue = false;
    }

    if (defaultValue !== undefined) {
      defaults[param.name] = defaultValue;
    }
  }

  return defaults;
}

/**
 * Checks if a parameter should be visible based on its dependencies
 */
export function isParameterVisible(
  definition: ParameterDefinition,
  values: Record<string, unknown>
): boolean {
  if (!definition.dependsOn) {
    return true;
  }

  const dependentValue = values[definition.dependsOn.parameter];
  return dependentValue === definition.dependsOn.value;
}
